import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './TransactionView.module.css';
import { FETCH_TRANSACTION_BY_DATE_URL } from '../constant/apiConstants';

const QUICK_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'prevMonth', label: 'Previous Month' },
  { key: 'quarter', label: 'This Quarter' },
  { key: 'fy', label: 'This FY' },
];

const TransactionView = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeFilter, setActiveFilter] = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [quarters, setQuarter] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState('');

  // Custom date modal
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const getUserData = () => {
    const user = sessionStorage.getItem('user');
    const token = sessionStorage.getItem('token');
    return {
      user: user ? JSON.parse(user) : null,
      token: token || null,
    };
  };

  const fetchTransactions = async (start, end) => {
    setLoading(true);
    setError('');
    try {
      const { user, token } = getUserData();
      if (!user || !token) {
        setError('Please login to view transactions');
        setLoading(false);
        return;
      }

      const requestData = {
        user,
        token,
        startDate: new Date(start),
        endDate: new Date(end),
      };

      const response = await axios.post(`${FETCH_TRANSACTION_BY_DATE_URL}`, requestData, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.status === 'success') {
        setTransactions(response.data.payload || []);
        setError('');
      } else if (response.data.status === 'not_found') {
        setTransactions([]);
        setError('No transactions found');
      } else {
        setError(response.data.message || 'Failed to fetch transactions');
        setTransactions([]);
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      if (err.response?.status === 401) {
        setError('Unauthorized access. Please login again.');
      } else {
        setError('Error fetching transactions. Please try again.');
      }
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = (period) => {
    const today = new Date();
    const start = new Date();
    switch (period) {
      case 'today':
        return { start: today, end: today };
      case 'week':
        start.setDate(today.getDate() - 6);
        return { start, end: today };
      case 'month':
        start.setMonth(today.getMonth());
        start.setDate(1);
        return { start, end: today };
      case 'prevMonth': {
        const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: prevMonth, end: prevMonthEnd };
      }
      case 'quarter': {
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        return { start: quarterStart, end: today };
      }
      case 'fy': {
        const fyStart = new Date(today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1, 3, 1);
        return { start: fyStart, end: today };
      }
      default:
        return { start: today, end: today };
    }
  };

  const handleQuickFilter = (period) => {
    const { start, end } = getDateRange(period);
    const startStr = start.toISOString().split('T')[0];
    const endStr = end.toISOString().split('T')[0];

    setStartDate(startStr);
    setEndDate(endStr);
    setActiveFilter(period);
    setSelectedQuarter('');
    fetchTransactions(startStr, endStr);
  };

  // Build current + previous FY quarters, each tagged with its FY group label.
  const getIndianFYQuarters = () => {
    const today = new Date();
    const currentFyStart = today.getMonth() < 3 ? today.getFullYear() - 1 : today.getFullYear();

    const makeQuarters = (startYear, group) => ({
      [`${group}-Q1`]: { label: `Q1 · Apr–Jun ${startYear}`, start: `${startYear}-04-01`, end: `${startYear}-06-30`, group },
      [`${group}-Q2`]: { label: `Q2 · Jul–Sep ${startYear}`, start: `${startYear}-07-01`, end: `${startYear}-09-30`, group },
      [`${group}-Q3`]: { label: `Q3 · Oct–Dec ${startYear}`, start: `${startYear}-10-01`, end: `${startYear}-12-31`, group },
      [`${group}-Q4`]: { label: `Q4 · Jan–Mar ${startYear + 1}`, start: `${startYear + 1}-01-01`, end: `${startYear + 1}-03-31`, group },
    });

    const fyLabel = (start) => `FY ${start}-${String(start + 1).slice(-2)}`;

    return {
      ...makeQuarters(currentFyStart, fyLabel(currentFyStart)),
      ...makeQuarters(currentFyStart - 1, fyLabel(currentFyStart - 1)),
    };
  };

  // Group quarters by their FY label so we can render <optgroup> separators.
  const quarterGroups = quarters
    ? Object.entries(quarters).reduce((acc, [key, q]) => {
        (acc[q.group] = acc[q.group] || []).push([key, q]);
        return acc;
      }, {})
    : {};

  const handleQuarterChange = (quarterKey) => {
    if (!quarters || !quarterKey) {
      setSelectedQuarter('');
      return;
    }
    const selected = quarters[quarterKey];
    if (selected) {
      setStartDate(selected.start);
      setEndDate(selected.end);
      setActiveFilter('quarter');
      setSelectedQuarter(quarterKey);
      fetchTransactions(selected.start, selected.end);
    }
  };

  const openCustomDateModal = () => {
    setCustomStart(startDate || '');
    setCustomEnd(endDate || '');
    setShowCustomDateModal(true);
  };

  const applyCustomDate = () => {
    if (!customStart || !customEnd) {
      alert('Please select both start and end dates.');
      return;
    }
    if (customStart > customEnd) {
      alert('Start date cannot be after end date.');
      return;
    }
    setStartDate(customStart);
    setEndDate(customEnd);
    setActiveFilter('custom');
    setSelectedQuarter('');
    setShowCustomDateModal(false);
    fetchTransactions(customStart, customEnd);
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  const formatAmount = (amount) => new Intl.NumberFormat('en-IN').format(Math.abs(Number(amount) || 0));

  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'BILL':
        return 'RETAIL';
      case 'EXPENSE':
        return 'EXPENSE';
      case 'INCOME':
        return 'INCOME';
      case 'TRANSFER':
        return 'TRANSFER';
      default:
        return type;
    }
  };

  const typeBadgeClass = (type) => {
    switch (type) {
      case 'BILL':
        return styles.badgeRetail;
      case 'EXPENSE':
        return styles.badgeExpense;
      case 'INCOME':
        return styles.badgeIncome;
      case 'TRANSFER':
        return styles.badgeTransfer;
      default:
        return '';
    }
  };

  useEffect(() => {
    setQuarter(getIndianFYQuarters());
    handleQuickFilter('today');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Local search filter + summary -----
  const filteredTransactions = transactions.filter((t) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      String(t.transactionId ?? '').toLowerCase().includes(q) ||
      String(t.description ?? '').toLowerCase().includes(q) ||
      String(getTransactionTypeLabel(t.transactionType) ?? '').toLowerCase().includes(q) ||
      String(t.mode ?? '').toLowerCase().includes(q) ||
      String(t.userId ?? '').toLowerCase().includes(q) ||
      String(t.storeId ?? '').toLowerCase().includes(q)
    );
  });

  const totalAmountSum = filteredTransactions.reduce((sum, t) => sum + (Number(t.amount) || 0), 0);

  return (
    <div className={styles.transactionView}>
      <h1 className={styles.heading}>Transactions</h1>

      {error && <p className={styles.error}>{error}</p>}

      {/* -------------------- Filter row -------------------- */}
      <div className={styles.filterRow}>
        <div className={styles.filters}>
          {QUICK_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              className={`${styles.filterButton} ${activeFilter === key ? styles.active : ''}`}
              onClick={() => handleQuickFilter(key)}
              disabled={loading}
            >
              {label}
            </button>
          ))}
          <button
            className={`${styles.filterButton} ${activeFilter === 'custom' ? styles.active : ''}`}
            onClick={openCustomDateModal}
            disabled={loading}
          >
            Custom Date
          </button>

          {quarters && (
            <div className={styles.quarterDropdown}>
              <select value={selectedQuarter} onChange={(e) => handleQuarterChange(e.target.value)} disabled={loading}>
                <option value="">Select Quarter</option>
                {Object.entries(quarterGroups).map(([group, entries]) => (
                  <optgroup key={group} label={group}>
                    {entries.map(([key, q]) => (
                      <option key={key} value={key}>
                        {q.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          )}
        </div>

        {startDate && endDate && (
          <div className={styles.dateChip}>
            <span>{formatDate(startDate)}</span>
            <span className={styles.arrow}>→</span>
            <span>{formatDate(endDate)}</span>
          </div>
        )}
      </div>

      {/* -------------------- Toolbar -------------------- */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <span className={styles.searchIcon}>🔍</span>
          <input
            className={styles.searchInput}
            type="text"
            placeholder="Search by id, description, type, payment, user or store"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className={styles.clearSearch} onClick={() => setSearchQuery('')}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* -------------------- Custom Date Modal -------------------- */}
      {showCustomDateModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h2>Select Date Range</h2>
            <button className={styles.modalClose} onClick={() => setShowCustomDateModal(false)}>
              ×
            </button>
            <div className={styles.dateInputs}>
              <label>
                Start Date
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
              </label>
              <label>
                End Date
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
              </label>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.findButton} onClick={applyCustomDate} disabled={loading}>
                Apply
              </button>
              <button className={styles.cancelButton} onClick={() => setShowCustomDateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------------------- Table -------------------- */}
      <div className={styles.tableWrap}>
        {loading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
            <span className={styles.loadingText}>Loading transactions…</span>
          </div>
        )}

        {filteredTransactions.length > 0 ? (
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Transaction Id</th>
                  <th>Date</th>
                  <th className={styles.wrapCell}>Description</th>
                  <th>Transaction Type</th>
                  <th>Payment Mode</th>
                  <th>Amount</th>
                  <th>User</th>
                  <th>Store</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map((transaction, index) => {
                  const amount = Number(transaction.amount) || 0;
                  return (
                    <tr key={`${transaction.transactionId}-${index}`}>
                      <td>{transaction.transactionId}</td>
                      <td>{transaction.date}</td>
                      <td className={styles.wrapCell}>{transaction.description}</td>
                      <td>
                        <span className={`${styles.badge} ${typeBadgeClass(transaction.transactionType)}`}>
                          {getTransactionTypeLabel(transaction.transactionType)}
                        </span>
                      </td>
                      <td>{transaction.mode || '-'}</td>
                      <td className={amount < 0 ? styles.negativeAmount : styles.amount}>
                        ₹{formatAmount(transaction.amount)}
                      </td>
                      <td>{transaction.userId || '-'}</td>
                      <td>{transaction.storeId || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          !loading && <p className={styles.empty}>{error || 'No transactions to display.'}</p>
        )}
      </div>

      {/* -------------------- Summary -------------------- */}
      {filteredTransactions.length > 0 && (
        <div className={styles.summaryBar}>
          <div className={styles.summaryItem}>
            <span>Total Transactions</span>
            <strong>{filteredTransactions.length}</strong>
          </div>
          <div className={`${styles.summaryItem} ${styles.summaryTotal}`}>
            <span>Total Amount</span>
            <strong>₹{new Intl.NumberFormat('en-IN').format(totalAmountSum)}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionView;